import { useCallback, useEffect, useState } from 'react'
import { getStoredExpiryTime, isExpired, SUBSCRIPTION_STORED_EVENT } from './useSubscription'
import { getUserPrefs, subscribeUserPrefs, updateUserPrefs } from './userPrefs'

/**
 * Premium ("Pro") app icon — web port of Android's AppIconManager
 * .reconcileForSubscription. On the apps it swaps the launcher icon; on web it
 * swaps the header logo and the favicon.
 *
 * Rule, evaluated on every load and whenever the subscription or the
 * preference changes:
 *  - no active subscription → default icon (the opt-out is left as is)
 *  - the subscription's expiry differs from the one the icon was last granted
 *    for (a renewal / new plan) → the opt-out is reset, so premium comes back
 *  - otherwise premium unless the merchant opted for default in Settings
 * The opt-out lives in utils/userPrefs and is wiped on sign-out.
 */
export const reconcileProIcon = (): boolean => {
  const expiry = getStoredExpiryTime()
  const active = expiry !== null && !isExpired(expiry)
  if (!active) {
    return false
  }
  const prefs = getUserPrefs().proIcon
  if (prefs.grantedExpiry !== expiry) {
    updateUserPrefs({ proIcon: { defaultOpted: false, grantedExpiry: expiry } })
    return true
  }
  return !prefs.defaultOpted
}

// Settings toggle: premium ⇄ default for the current grant
export const setProIconEnabled = (enabled: boolean): void => {
  updateUserPrefs({ proIcon: { defaultOpted: !enabled, grantedExpiry: getStoredExpiryTime() } })
}

export interface IProIcon {
  // The merchant currently sees the premium icon
  premium: boolean
  // A subscription is active, so the toggle in Settings is meaningful
  subscribed: boolean
  setEnabled: (enabled: boolean) => void
}

export const useProIcon = (): IProIcon => {
  // Always start as the server rendered it (default) and switch after mount:
  // React hydration keeps server-rendered attributes, so an initial `true`
  // here would leave the <img src> pointing at the default logo with no
  // re-render to correct it.
  const [premium, setPremium] = useState<boolean>(false)
  const [subscribed, setSubscribed] = useState<boolean>(() => {
    const expiry = typeof window === 'undefined' ? null : getStoredExpiryTime()
    return expiry !== null && !isExpired(expiry)
  })

  useEffect(() => {
    const evaluate = () => {
      const expiry = getStoredExpiryTime()
      setSubscribed(expiry !== null && !isExpired(expiry))
      setPremium(reconcileProIcon())
    }
    evaluate()
    const unsubscribe = subscribeUserPrefs(evaluate)
    window.addEventListener(SUBSCRIPTION_STORED_EVENT, evaluate)
    window.addEventListener('storage', evaluate)
    return () => {
      unsubscribe()
      window.removeEventListener(SUBSCRIPTION_STORED_EVENT, evaluate)
      window.removeEventListener('storage', evaluate)
    }
  }, [])

  const setEnabled = useCallback((enabled: boolean) => setProIconEnabled(enabled), [])

  return { premium, subscribed, setEnabled }
}
